import { Link } from 'react-router'
import { Badge } from '#app/components/ui/badge.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { type SeoProps } from '#app/seo/seo.tsx'

const TOOLS = [
	{
		to: '/tools/dumpster-size-calculator',
		icon: 'truck' as const,
		iconColor: 'text-amber-600 dark:text-amber-400',
		iconBg: 'bg-amber-100 dark:bg-amber-900/50',
		borderColor: 'border-t-amber-500',
		title: 'Dumpster Size & Cost Calculator',
		description:
			'Find the right dumpster size for your project and compare prices from DFW vendors. Covers 10 to 40-yard roll-offs with real vendor pricing.',
		features: [
			'Size recommendations by project type',
			'Side-by-side vendor price comparison',
			'DFW permit requirements by city',
		],
		cta: 'Use calculator',
	},
	{
		to: '/tools/equipment-cost-estimator',
		icon: 'drill' as const,
		iconColor: 'text-blue-600 dark:text-blue-400',
		iconBg: 'bg-blue-100 dark:bg-blue-900/50',
		borderColor: 'border-t-blue-500',
		title: 'Equipment & Tool Rental Cost Estimator',
		description:
			'Compare daily, weekly, and monthly rental rates for 40+ equipment types across Dallas-Fort Worth — from mini excavators to floor sanders.',
		features: [
			'8 categories, 40+ equipment types',
			'Real DFW vendor rates side by side',
			'Project guides with equipment checklists',
		],
		cta: 'Compare prices',
	},
] as const

export const handle = {
	seo({ origin }: { origin: string }): SeoProps {
		const canonical = `${origin}/tools`
		return {
			title: 'DFW Rental Tools & Calculators | RentDFW',
			description:
				'Free interactive tools and calculators for Dallas-Fort Worth rentals. Find the right dumpster size, compare vendor prices, and more.',
			canonical,
			openGraph: {
				title: 'DFW Rental Tools & Calculators',
				description:
					'Free interactive tools and calculators for Dallas-Fort Worth rentals.',
				type: 'website',
				url: canonical,
			},
			twitter: {
				card: 'summary',
				title: 'DFW Rental Tools & Calculators',
				description:
					'Free interactive tools and calculators for Dallas-Fort Worth rentals.',
			},
		}
	},
}

export default function ToolsIndex() {
	return (
		<div className="container mx-auto px-4 py-12 sm:px-6 lg:px-8">
			<div className="mx-auto max-w-4xl">
				<div className="text-center">
					<Badge variant="secondary" className="mb-4">
						Free Tools
					</Badge>
					<h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
						DFW Rental Tools & Calculators
					</h1>
					<p className="text-muted-foreground mx-auto mt-4 max-w-2xl text-lg">
						Stop guessing on rental costs. These free tools pull real DFW vendor
						pricing so you can compare rates and pick the right equipment before
						you call.
					</p>
				</div>

				<div className="mt-12 grid gap-6 sm:grid-cols-2">
					{TOOLS.map((tool) => (
						<Link key={tool.to} to={tool.to} className="group">
							<div
								className={`bg-card h-full rounded-lg border border-t-4 ${tool.borderColor} p-6 transition-all group-hover:shadow-md`}
							>
								<div className="flex items-center gap-3">
									<div
										className={`flex size-10 shrink-0 items-center justify-center rounded-full ${tool.iconBg}`}
									>
										<Icon
											name={tool.icon}
											size="md"
											className={tool.iconColor}
										/>
									</div>
									<h2 className="text-lg font-semibold">{tool.title}</h2>
								</div>

								<p className="text-muted-foreground mt-3 text-sm">
									{tool.description}
								</p>

								<ul className="mt-4 space-y-1.5">
									{tool.features.map((feature) => (
										<li
											key={feature}
											className="text-muted-foreground flex items-center gap-2 text-sm"
										>
											<Icon
												name="check"
												size="sm"
												className="shrink-0 text-green-500"
											/>
											{feature}
										</li>
									))}
								</ul>

								<div className="mt-5">
									<span className="text-primary text-sm font-medium group-hover:underline">
										{tool.cta} &rarr;
									</span>
								</div>
							</div>
						</Link>
					))}
				</div>
			</div>
		</div>
	)
}
